import { ManagerState } from './../manager.state';
import { State as ModalState} from './../../shared/modal/store';
import {EventEmitter, Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {catchError, delay, exhaustMap, filter, map, mergeMap, switchMap, tap} from 'rxjs/operators';
import {concat, forkJoin, of, Subject} from 'rxjs';
import * as _ from 'lodash';

// Payload Types
import {BaseStateV2} from '../../core/base.state';
import {Service} from './service';
import {CoreStore} from '../../core/core.store';
import * as FolderStore from '../folder/store';
import {toQueryString} from '../../core/services/utils';
import * as UILoaderStore from '../../shared/loader/store';
import {Action, createFeatureSelector, createSelector, Store, select} from '@ngrx/store';

import { ToastrService } from 'ngx-toastr';

export interface IFileUploadItem {
    parent: any;
    file: any;
    path: string;
    form: any;
    signedUrl: string;
    onUploadEvent: Subject<any>
}

export const FEATURE_NAME = 'FILE';
export const RESOURCE: string = 'files';

interface IUploadProgress {
    fileId: string;
    percent: number;
}

interface IDownloadProgress {
    fileId: string;
    percent: number;
}

export const FeatureActionTypes = {
    ...CoreStore.baseActionTypesBuilder(FEATURE_NAME),
    UploadingInProgress: `[${FEATURE_NAME}]` + " UploadingInProgress",
    UploadingInProgressComplete: `[${FEATURE_NAME}]` + " UploadingInProgressComplete",
    UploadFiles: `[${FEATURE_NAME}]` + " UploadingInProgressComplete",
    DownloadingInProgress: `[${FEATURE_NAME}]` + " DownloadingInProgress",
    DownloadingInProgressComplete: `[${FEATURE_NAME}]` + " DownloadingInProgressComplete",
    DownloadFiles: `[${FEATURE_NAME}]` + " DownloadingInProgressComplete"
};

class IFeatureActions extends CoreStore.Actions {

    UploadingInProgress: Action | any;
    UploadingInProgressComplete: Action | any;
    DownloadingInProgress: Action | any;
    DownloadingInProgressComplete: Action | any;

    constructor(featureName: string){
        super(featureName);
        this.buildFeatureActions();
    }

    buildFeatureActions(){
        this._types = _.merge(this._types, FeatureActionTypes);
        this.UploadingInProgress = class implements Action {
            public readonly type: any = FeatureActions.UploadingInProgress;
            constructor(public payload: IUploadProgress) {}
        };
        this.UploadingInProgressComplete = class implements Action {
            public readonly type: any = FeatureActions.UploadingInProgressComplete;
            constructor(public payload: any) {}
        };
        this.DownloadingInProgress = class implements Action {
            public readonly type: any = FeatureActions.DownloadingInProgress;
            constructor(public payload: IUploadProgress) {}
        };
        this.DownloadingInProgressComplete = class implements Action {
            public readonly type: any = FeatureActions.DownloadingInProgressComplete;
            constructor(public payload: any) {}
        };
    }

}

export const FeatureActions:IFeatureActions     = new IFeatureActions(FEATURE_NAME);

export interface IState extends CoreStore.State {
    uploading: Array<IUploadProgress>;
    downloading: Array<IDownloadProgress>;
    file: any
}

export const initialState: IState = {
    ...CoreStore.initialState,
    uploading: [],
    downloading: [],
    file: undefined
};


export function reducer(state = initialState, action: any): any {
    switch (action.type) {

        case FeatureActionTypes.UploadingInProgress: {

            return {
                ...state,
                uploading: _.flatten([
                    state.uploading.filter(f => f.fileId !== action.payload.fileId ),
                    action.payload
                ])
            }
        }

        case FeatureActionTypes.UploadingInProgressComplete: {
            return {
                ...state,
                uploading: state.uploading
                    .filter(f => f.fileId !== action.payload.fileId )
            }
        }

        case FeatureActionTypes.DownloadingInProgress: {

            return {
                ...state,
                downloading: _.flatten([
                    state.downloading.filter(f => f.fileId !== action.payload.fileId ),
                    action.payload
                ])
            }
        }

        case FeatureActionTypes.DownloadingInProgressComplete: {
            return {
                ...state,
                downloading: state.downloading
                    .filter(f => f.fileId !== action.payload.fileId )
            }
        }

        default: {
            return CoreStore.reducer(state, action, FeatureActions)
        }

    }
}

const selectState = createFeatureSelector<IState>(FEATURE_NAME);

export const selectors = {
    ...CoreStore.selectors(FEATURE_NAME),
    uploading: createSelector(selectState, (state: IState) => state.uploading),
    downloading: createSelector(selectState, (state: IState) => state.downloading),
    file: createSelector(selectState, (state: IState) => state.file),
};


// Effects

@Injectable()
export class Effects {

    private _coreEffectsOptions: CoreStore.EffectsOptions = {
        FEATURE_NAME: FEATURE_NAME,
        Actions: FeatureActions,
        featureService: this._svc,
        actions$: this.actions$
    };

    /*

    @Effect()
    Load$ = CoreStore.Effects.Load(
        this._coreEffectsOptions,
        (obj) => _.flatten([obj])[0]
    );
    */

    @Effect()
    LoadMultiple$ = this._coreEffectsOptions.actions$.pipe(
        ofType(this._coreEffectsOptions.Actions.Types.Load),
        map((action: any) => action.payload),
        exhaustMap((obj) =>
            forkJoin(..._.map(_.flatten([obj]), obj => this._svc.doLoad(obj)))
                .pipe(
                    map(data => {
                        let obj = {};
                        if(data[0].parent === undefined) {
                            obj = {
                                parent: {
                                    id: data[0].parentId
                                }
                            };
                        }
                        else {
                            obj = {
                                parent: {
                                    id: data[0].parent
                                }
                            };
                        }
                        return new this._coreEffectsOptions.Actions.LoadSuccess(obj);
                    }),
                    catchError(error => of(new this._coreEffectsOptions.Actions.LoadFailure(error)))
                ),
        )
    );

    currentState: string;
    rootFolderId: string;
    rootFolderPath: string;
    currentFolderId: string;

   @Effect({dispatch: false})
   CreateV3$ = this._coreEffectsOptions.actions$.pipe(
       ofType(this._coreEffectsOptions.Actions.Types.Create),
       map((action: any) => {
            this.currentState = action.payload.currentState;
            this.rootFolderId = action.payload.rootFolderId;
            this.rootFolderPath = action.payload.rootFolderPath;
            this.currentFolderId = action.payload.currentFolderId;
            if(this.currentState !== 'myFiles') {
                for(let i = 0; i < action.payload.length; i++) {
                    action.payload[i].path = action.payload.rootFolderPath;
                    action.payload[i].parent.id = action.payload.rootFolderId;
                }
            }
            return action.payload;
       }),
       switchMap((obj) =>
           forkJoin(..._.map(_.flatten([obj]), obj =>
               this._svc.stage(obj)
                   .pipe(
                       map((data: any) => {
                           let fileItem = _.find(_.flatten([obj]), (i) => i.file.name === data.name);
                           return _.merge(data, fileItem)
                       }),
                       catchError(error => of(new this._coreEffectsOptions.Actions.CreateFailure(error)))
                   )
           ))
       ),
        tap((response) => {
            let obj = {};
            switch(this.currentState){
                case 'myFiles':
                    if(this.currentFolderId === this.rootFolderId) {
                        this.store.dispatch((new FolderStore.FeatureActions.Load({
                            id: this.currentFolderId,
                            loadMyFiles: true
                        })));
                    }
                    else {
                        this.store.dispatch({
                            type: FeatureActionTypes.CreateSuccess,
                            payload: response[0]
                        });
                    }

                    break;
                case 'bookmarks':
                    if (this.currentFolderId === this.rootFolderId) {
                        obj={
                            id: this.currentFolderId,
                            loadBookmarks: true
                        }
                    }
                    else {
                        obj={
                            id: this.currentFolderId
                        }
                    }
                    this.store.dispatch((new FolderStore.FeatureActions.Load(obj)));
                    break;
                case 'recent':
                    if (this.currentFolderId === this.rootFolderId) {
                        obj={
                            id: this.currentFolderId,
                            loadRecentFiles: true
                        }
                    }
                    else {
                        obj={
                            id: this.currentFolderId
                        }
                    }
                    this.store.dispatch((new FolderStore.FeatureActions.Load(obj)));
                    break;
                case 'sharedWithMe':
                    if (this.currentFolderId === this.rootFolderId) {
                        obj={
                            id: this.currentFolderId,
                            loadFoldersSharedWithMe: true
                        }
                    }
                    else {
                        obj={
                            id: this.currentFolderId
                        }
                    }
                    this.store.dispatch((new FolderStore.FeatureActions.Load(obj)));
                    break;
                case 'sharedWithOthers':
                    if (this.currentFolderId === this.rootFolderId) {
                        obj={
                            id: this.currentFolderId,
                            loadFoldersSharedWithOthers: true
                        }
                    }
                    else {
                        obj={
                            id: this.currentFolderId
                        }
                    }
                    this.store.dispatch((new FolderStore.FeatureActions.Load(obj)));
                    break;
            }
        }),
       delay(1000),
       tap((data) => {
           _.map(data, (fileItem) => {
               this._svc.uploadV3(fileItem).toPromise().then(
                   (data) => {
                        this.toastrService.success('', 'The file has been uploaded successfully.', {
                            timeOut: 3000
                        });
                   },
                   (error) => {
                        this.toastrService.error(
                            '<b>If the problem persists, please contact an administrator.</b>',
                            'An error occured while uploading the file. Please try again.',
                            {
                                closeButton: true,
                                disableTimeOut: true,
                                enableHtml: true
                            }
                        );
                   }
               );
           })
       }),
   );


   /*
    @Effect()
    CreateV2$ = this._coreEffectsOptions.actions$.pipe(
        ofType(this._coreEffectsOptions.Actions.Types.Create),
        map((action: any) => action.payload),
        exhaustMap((obj) =>
            forkJoin(..._.map(_.flatten([obj]), obj => this._svc.uploadV2(obj)))
                .pipe(
                    map(data => new this._coreEffectsOptions.Actions.CreateSuccess(data[0])),
                    catchError(error => of(new this._coreEffectsOptions.Actions.CreateFailure(error)))
                ),
        )
    );

    /*@Effect()
    Create$ = CoreStore.Effects.Create(this._coreEffectsOptions,
        (obj) => _.flatten([obj]));*/

    //new this._coreEffectsOptions.Actions.CreateSuccess(data)
    /*
    @Effect()
    Create$ = this._coreEffectsOptions.actions$.pipe(
        ofType(this._coreEffectsOptions.Actions.Types.Create),
        map((action: any) => action.payload),
        switchMap((obj) =>
            this._coreEffectsOptions.featureService.doAdd(_.flatten([obj]))
                .pipe(
                    map(data => forkJoin(...this._svc.uploadS3(data[0], data[1]))
                        .pipe(catchError(error => of(new this._coreEffectsOptions.Actions.CreateFailure(error))))
                    ),
                    catchError(error => of(new this._coreEffectsOptions.Actions.CreateFailure(error))))
        ),
        exhaustMap((response) =>
                response.pipe(
                    map((data) => new this._coreEffectsOptions.Actions.CreateSuccess(data[0])),
                    catchError(error => of(new this._coreEffectsOptions.Actions.CreateFailure(error)))
                )
        )
    );
    */

    /*
    @Effect()
    Delete$ = CoreStore.Effects.Delete(
        this._coreEffectsOptions,
        (obj) => RESOURCE + '?' + toQueryString(obj)
    );*/

    @Effect()
    Delete$ = CoreStore.Effects.Delete(
        this._coreEffectsOptions,
        (obj) => RESOURCE + '?' + toQueryString(obj)
    );

    @Effect()
    DeleteMultiple$ = this._coreEffectsOptions.actions$.pipe(
        ofType(this._coreEffectsOptions.Actions.Types.DeleteMultiple),
        map((action: any) => action.payload),
        exhaustMap((obj) =>
        this._coreEffectsOptions.featureService.doDeleteMultiple({toDeleteIds: obj.toDeleteIds}, "deleteMultiple")
                .pipe(
                    map(data => {
                        this.toastrService.success('', 'Item(s) has been deleted successfully.', {
                            timeOut: 3000
                        });
                        if(obj.isMyFilesSection) {
                            return (new FolderStore.FeatureActions.Load({
                                id: data.parent.id,
                                loadMyFiles: true
                            }));
                        }
                        else {
                            return (new this._coreEffectsOptions.Actions.DeleteSuccess(data));
                        }
                    }),
                    catchError(error => {
                        this.toastrService.error(
                            '<b>If the problem persists, please contact an administrator.</b>',
                            'An error occured while deleting the item. Please try again.',
                            {
                                closeButton: true,
                                disableTimeOut: true,
                                enableHtml: true
                            }
                        );
                        return (of(new this._coreEffectsOptions.Actions.DeleteFailure(error)));
                    })
                ),
        )
    );

    @Effect()
    Update$ = this._coreEffectsOptions.actions$.pipe(
        ofType(this._coreEffectsOptions.Actions.Types.Update),
        map((action: any) => action.payload),
        exhaustMap((obj) =>
        this._coreEffectsOptions.featureService.doUpdate({id: obj.id, name: obj.name, path: obj.path, type: obj.type, oldName: obj.oldName, oldPath: obj.oldPath}, RESOURCE + "/rename")
            .pipe(
                map(data => {
                    this.toastrService.success('', 'The item has been renamed successfully.', {
                        timeOut: 3000
                    });
                    if(obj.isMyFilesSection) {
                        return (new FolderStore.FeatureActions.Load({
                            id: data.parent.id,
                            loadMyFiles: true
                        }));
                    }
                    else {
                        return (new this._coreEffectsOptions.Actions.UpdateSuccess(data));
                    }
                }),
                catchError(error => {
                    this.toastrService.error(
                        '<b>If the problem persists, please contact an administrator.</b>',
                        'An error occured while renaming the item. Please try again.',
                        {
                            closeButton: true,
                            disableTimeOut: true,
                            enableHtml: true
                        }
                    );
                    return (of(new this._coreEffectsOptions.Actions.UpdateFailure(error)));
                }))
        )
    );

    @Effect()
    Paste$ = this._coreEffectsOptions.actions$.pipe(
        ofType(this._coreEffectsOptions.Actions.Types.Paste),
        map((action: any) => action.payload),
        exhaustMap((obj) =>
        this._coreEffectsOptions.featureService.doPaste({copiedItemIds: obj.copiedItemIds, destinationItemId: obj.destinationItemId}, "paste")
            .pipe(
                map(data => {
                    if(data.pasted === true) {
                        this.toastrService.success('', 'The item has been copied successfully.', {
                            timeOut: 3000
                        });
                    }
                    if(data.eliminatedItemName) {
                        this.toastrService.error(
                            '',
                            'The folder '+ data.eliminatedItemName + ' cannot be copied. The destination folder is a subfolder of this folder.',
                            {
                                closeButton: true,
                                disableTimeOut: true,
                                enableHtml: true
                            }
                        );
                    }
                    if(obj.isMyFilesSection) {
                        return (new FolderStore.FeatureActions.Load({
                            id: obj.currentFolderId,
                            loadMyFiles: true
                        }));
                    }
                    else {
                        return (new FolderStore.FeatureActions.Load({
                            id: obj.currentFolderId
                        }));
                    }
                }),
                catchError(error => {
                    this.toastrService.error(
                        '<b>If the problem persists, please contact an administrator.</b>',
                        'An error occured while making a copy of the item. Please try again.',
                        {
                            closeButton: true,
                            disableTimeOut: true,
                            enableHtml: true
                        }
                    );
                    return (of(new this._coreEffectsOptions.Actions.PasteFailure(error)));
                }))
        )
    );

    @Effect({ dispatch: false })
    Move$ = this._coreEffectsOptions.actions$.pipe(
        ofType(this._coreEffectsOptions.Actions.Types.Move),
        map((action: any) => action.payload),
        exhaustMap((obj) =>
        this._coreEffectsOptions.featureService.doMove({movingItemId: obj.movingItemId, destinationItemId: obj.destinationItemId}, "move")
            .pipe(
                map(data => {
                    if(data.exist === true) {
                        this.modalState.navigation.select("MoveConflictAlertComponent");
                        setTimeout(() => {
                            this.modalState.open()
                        });
                    }
                    else {
                        this.toastrService.success('', 'The item has been moved successfully.', {
                            timeOut: 3000
                        });
                    }
                }),
                catchError(error => {
                    this.toastrService.error(
                        '<b>If the problem persists, please contact an administrator.</b>',
                        'An error occured while moving the item. Please try again.',
                        {
                            closeButton: true,
                            disableTimeOut: true,
                            enableHtml: true
                        }
                    );
                    return error;
                })
            )
        )
    );

    @Effect()
    ActionSuccess$ = CoreStore.Effects.ActionSuccess(
        this._coreEffectsOptions
    );

    @Effect()
    ActionFailure$ = CoreStore.Effects.ActionFailure(
        this._coreEffectsOptions
    );

    @Effect()
    SuccessReset$ = CoreStore.Effects.SuccessReset(
        this._coreEffectsOptions
    );


    @Effect()
    FlushOnLogout$ = CoreStore.Effects.FlushOnLogout(this._coreEffectsOptions);


    @Effect()
    ReloadFolders$ = this.actions$.pipe(
        ofType(FeatureActions.Types.DeleteSuccess, FeatureActionTypes.CreateSuccess, FeatureActionTypes.UpdateSuccess),
        map((action: any) => action.payload),
        exhaustMap((file) => of(new FolderStore.FeatureActions.Load({id: file.parent.id})))
    );

    //TODO: need better way of manipulating another Feature's store
    @Effect()
    StartUILoader$ = this.actions$.pipe(
        ofType(FeatureActions.Types.Create,
            FeatureActions.Types.Delete,
            FeatureActions.Types.Update),
        exhaustMap(() => of({
            type: UILoaderStore.FeatureActions.Types.Set,
            payload: {isLoading: true}
        }))
    );

    @Effect()
    EndUILoader$ = this.actions$.pipe(
        ofType(
            FeatureActions.Types.Success,
            FeatureActions.Types.Failure
        ),
        exhaustMap(() => of({
            type: UILoaderStore.FeatureActions.Types.Set,
            payload: {isLoading: false}
        }))
    );

    constructor(protected actions$: Actions<any>, protected _svc: Service, protected store: Store<State>, public modalState: ModalState, public toastrService: ToastrService) {}
}

@Injectable()
export class State extends BaseStateV2 {

    FEATURE_NAME: string = FEATURE_NAME;
    FeatureActions: CoreStore.Actions = FeatureActions;
    _selectors = selectors;

    async download(fileItem) {
        return this.load(fileItem);
    }

    async upload(fileItem: IFileUploadItem) {
        return this.create(fileItem);
    }

    uploadMultiple(fileItems: Array<IFileUploadItem>, currentState, rootFolderId, rootFolderPath, currentFolderId) {
        Object.assign(fileItems, {
            currentState: currentState,
            rootFolderId: rootFolderId,
            rootFolderPath: rootFolderPath,
            currentFolderId: currentFolderId
        });

        return this.create(fileItems);
    }

    delete(fileItem) {
        return super.remove(fileItem);
    }

    deleteMultiple(fileItem) {
        return super.removeMultiple(fileItem);
    }

    isUploading(fileItem) {
        return this._store
            .select(selectors.uploading)
            .pipe(
                map((uploading: any) => {
                    return _.find(uploading, (uploadItem: IUploadProgress) => uploadItem.fileId === fileItem.id )
                })
            )
    }

    isDownloading(fileItem) {
        return this._store
            .select(selectors.downloading)
            .pipe(
                map((downloading: any) => {
                    return _.find(downloading, (downloadItem: IDownloadProgress) => downloadItem.fileId === fileItem.id )
                })
            )
    }

    set file(file) {
        this.set({file: file})
    }

    get file(){
        return this._store
            .pipe(select(this._selectors.file))
            .pipe(filter(exists => exists))
    }

    activate(fileItem){
        this.file = fileItem;
    }
}
